import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseComponent } from '../../types';
import { OrderTrackingCard } from './OrderTrackingCard';
import { GMVPerformanceCard } from './GMVPerformanceCard';
import { AIAutomationCard } from './AIAutomationCard';

interface HeroSectionProps extends BaseComponent {}

export function HeroSection({ className }: HeroSectionProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section
      data-hero-section
      className={`w-full min-h-screen lg:h-screen flex-shrink-0 relative overflow-hidden ${className || ''}`}
      style={{
        alignSelf: 'stretch',
        background:
          'linear-gradient(180deg, #020816 70%, #025BCF 90%, #0091BD 98%, rgba(2, 8, 22, 1) 100%)',
      }}
    >
      <div
        className="w-full h-full flex items-start"
        style={{ paddingTop: '80px' }}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @media (min-width: 1024px) {
            .hero-content {
              padding-top: 140px !important;
            }
          }
        `,
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Sol taraf - Ana içerik */}
            <div className="text-white">
              <h1
                className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[56px] leading-tight lg:leading-[64px] mb-6 transition-all duration-700 ease-out ${
                  isLoaded
                    ? 'opacity-100 transform translate-y-0'
                    : 'opacity-0 transform translate-y-8'
                }`}
                style={{
                  alignSelf: 'stretch',
                  fontFamily: '"Source Serif Pro"',
                  fontStyle: 'normal',
                  fontWeight: '600',
                }}
              >
                <span
                  style={{
                    color: 'var(--Brand, #76BCE8)',
                  }}
                >
                  E-Ticaret
                </span>
                <br />
                <span
                  style={{
                    color: 'var(--Text-White, #FFF)',
                  }}
                >
                  Operasyonlarınızı
                  <br />
                  Akıllı Şekilde Yönetin
                </span>
              </h1>

              <p
                className={`text-base lg:text-lg leading-6 lg:leading-7 mb-6 lg:mb-8 max-w-md lg:max-w-lg transition-all duration-700 ease-out ${
                  isLoaded
                    ? 'opacity-100 transform translate-y-0'
                    : 'opacity-0 transform translate-y-8'
                }`}
                style={{
                  color: '#989898',
                  fontFamily: 'Manrope',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  transitionDelay: '200ms',
                }}
              >
                Veriye dayalı içgörülerle satışlarınızı artırın, süreçlerinizi
                kolaylaştırın.
              </p>

              <button
                onClick={() => navigate('/contact')}
                className={`flex h-10 lg:h-[40px] px-4 lg:px-5 justify-center items-center gap-0.5 rounded-lg border-0 text-white text-sm lg:text-base font-medium leading-6 lg:leading-[26px] cursor-pointer transition-all duration-700 ease-out ${
                  isLoaded
                    ? 'opacity-100 transform translate-y-0 scale-100'
                    : 'opacity-0 transform translate-y-8 scale-95'
                }`}
                style={{
                  background:
                    'var(--Button-Blue-Gradient, linear-gradient(180deg, #025BCF 0%, #0091BD 100%))',
                  fontFamily: 'Manrope',
                  fontStyle: 'normal',
                  transitionDelay: '400ms',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background =
                    'linear-gradient(180deg, #0091BD 0%, #025BCF 100%)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background =
                    'linear-gradient(180deg, #025BCF 0%, #0091BD 100%)';
                }}
              >
                Ücretsiz Deneyin
              </button>
            </div>

            {/* Sağ taraf - Dashboard Cards */}
            <div className="relative h-64 sm:h-80 lg:h-96 mt-8 lg:mt-0">
              <OrderTrackingCard />
              <GMVPerformanceCard />
              <AIAutomationCard />
            </div>
          </div>

          {/* Alt kısım - Logo Grid */}
          <div className="absolute bottom-4 lg:bottom-16 left-0 right-0">
            <div className="relative z-10 py-4 lg:py-12">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
                <p
                  className="text-lg lg:text-xl mb-6 lg:mb-8"
                  style={{
                    color: '#73D0B9',
                    textAlign: 'center',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: 'normal',
                  }}
                >
                  20'den fazla girişim ve ajansın tercihi
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-blue-500/20 to-transparent"></div>
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
      </div>
    </section>
  );
}
